import { useEffect, useMemo, useState } from "react";
import { BoundarySelectContext } from "./BoundarySelectContext";
import { BoundarySelection } from "../../types/Boundary/BoundarySelection";
import * as OlExtent from "ol/extent";
import olFeature from "ol/Feature";
import olGeometry from "ol/geom/Geometry";
import olVectorSource from "ol/source/Vector";
import { Fill, Stroke, Style } from "ol/style";
import { ToolsType } from "../../constant/ToolsType";
import { BufferUnit } from "../../constant/BufferUnit";
import { OLParser } from "../reactOL/JSTS/OL";
import proj4 from "proj4";
import { transformExtent } from "ol/proj";
import { register } from "ol/proj/proj4";
import { WfsWGS84BoundingBox } from "../../types/Wfs/WfsCapabilities";
import { useWfsService } from "../../hooks";
import { LocalStorageKeys } from "../../types/LocalStorageKeys";
import { DateTime } from "luxon";
import { useAuthContext } from "../auth/AuthContext";
import { StoredBoundaryState } from "../../types/Boundary/StoredBoundaryState";
import { useTenantContext } from "../../context/Tenant/TenantContext";
import { useMaintenanceModeContext } from "../../context/MaintenanceMode/MaintenanceModeContext";
import { Roles } from "../../constant/Roles";

export interface BoundarySelectProviderProps {
  children?: React.ReactNode;
}

interface FeatureType {
  typeName: string;
  bbox: OlExtent.Extent;
}

const saveBoundaryState = (boundaries: BoundarySelection[]) => {
  const boundariesToStore = boundaries.map((b) => ({
    ...b,
    geometry: b.geometry.getCoordinates(),
  }));

  const storedBoundaries: StoredBoundaryState = {
    time: DateTime.local().toISO(),
    boundaries: boundariesToStore,
  };

  localStorage.setItem(
    LocalStorageKeys.BOUNDARIES,
    JSON.stringify(storedBoundaries),
  );
};

const BoundarySelectProvider = ({ children }: BoundarySelectProviderProps) => {
  const { config } = useTenantContext();
  const { isMaintenance } = useMaintenanceModeContext();

  const attributions =
    config.name === "Inrix" ?
      "&copy; Basemap Ltd. &copy; Inrix"
    : "&copy; Basemap Ltd.";

  const vectorSource = useMemo(() => new olVectorSource({ attributions }), []);
  const { user } = useAuthContext();
  const [boundaries, setBoundaries] = useState<BoundarySelection[]>([]);
  const [activeTool, setActiveTool] = useState<ToolsType>(ToolsType.NONE);
  const [viewExtent, setViewExtent] = useState<OlExtent.Extent | undefined>(
    undefined,
  );
  const [configBoundaryId, setConfigBoundaryId] = useState<
    string | number | undefined
  >(undefined);
  const [featureTypes, setFeatureTypes] = useState<FeatureType[] | undefined>();
  const [availableFeatureTypes, setAvailableFeatureTypes] = useState<string[]>(
    [],
  );

  const wfsService = useWfsService();

  const boundaryStyle = new Style({
    stroke: new Stroke({
      color: "#00587c",
      width: 2,
    }),
    fill: new Fill({
      color: "rgba(130, 146,155, 0.5)",
    }),
  });

  const selectedBoundaryStyle = new Style({
    stroke: new Stroke({
      color: "#00587c",
      width: 2,
    }),
    fill: new Fill({
      color: "rgba(130, 146,155, 0.90)",
    }),
  });

  const addBoundary = (boundary: BoundarySelection) => {
    if (boundaries.find((b) => b.boundaryId === boundary.boundaryId)) return;

    const feature = new olFeature({
      id: boundary.boundaryId,
      geometry: boundary.geometry as olGeometry,
      name: boundary.name,
    });
    feature.setStyle(boundaryStyle);
    vectorSource.addFeature(feature);

    const newBoundaries = [...boundaries, boundary];
    saveBoundaryState(newBoundaries);
    setBoundaries(newBoundaries);
    setViewExtent(vectorSource.getExtent());
  };

  const addBoundaries = (addboundaries: BoundarySelection[]) => {
    const dictinctBoundaries = addboundaries.filter((boundary) => {
      if (boundaries.find((b) => b.boundaryId === boundary.boundaryId))
        return false;
      const feature = new olFeature({
        id: boundary.boundaryId,
        geometry: boundary.geometry as olGeometry,
        name: boundary.name,
      });
      feature.setStyle(boundaryStyle);
      vectorSource.addFeature(feature);
      return true;
    });

    if (dictinctBoundaries.length > 0) {
      const newBoundaries = [...boundaries, ...dictinctBoundaries];
      saveBoundaryState(newBoundaries);
      setBoundaries(newBoundaries);
      setViewExtent(vectorSource.getExtent());
    }
  };

  const removeBoundary = (id: string | number) => {
    const feature = vectorSource.getFeatures().find((f) => f.get("id") === id);
    if (feature) {
      vectorSource.removeFeature(feature);
    }

    const newBoundaries = boundaries.filter((b) => b.boundaryId !== id);
    setBoundaries(newBoundaries);
    saveBoundaryState(newBoundaries);
  };

  const clearBoundaries = () => {
    saveBoundaryState([]);
    setBoundaries([]);
    vectorSource.clear();
  };

  const selectBoundary = (id: string | number) => {
    var feature = vectorSource.getFeatures().find((f) => f.get("id") === id);
    if (feature) {
      feature.setStyle(selectedBoundaryStyle);
    }
  };

  const deselectBoundary = (id: string | number) => {
    var feature = vectorSource.getFeatures().find((f) => f.get("id") === id);
    if (feature) {
      feature.setStyle(boundaryStyle);
    }
  };

  const bufferBoundary = (
    id: string | number,
    distance: number,
    unit: BufferUnit,
  ) => {
    const boundary = boundaries.find((b) => b.boundaryId === id);
    var feature = vectorSource.getFeatures().find((f) => f.get("id") === id);
    if (!boundary || !feature) return;

    if (distance === 0) {
      _resetBoundary(boundary, feature);
      setBoundaries(boundaries);
    } else if (
      distance !== boundary.bufferDistance ||
      unit !== boundary.bufferUnit
    ) {
      _bufferBoundary(distance, unit, boundary, feature);
      setBoundaries(boundaries);
    }
  };

  const _resetBoundary = (boundary: BoundarySelection, feature: olFeature) => {
    boundary.geometry = boundary.originalGeometry ?? boundary.geometry;
    boundary.bufferDistance = undefined;
    boundary.bufferDistanceMetres = undefined;
    boundary.bufferUnit = undefined;
    boundary.originalGeometry = undefined;
    feature.setGeometry(boundary.geometry);
  };

  const _bufferBoundary = (
    distance: number,
    unit: BufferUnit,
    boundary: BoundarySelection,
    feature: olFeature,
  ) => {
    const bufferDistanceMetres = _distanceInMetres(distance, unit);
    boundary.bufferDistance = distance;
    boundary.bufferDistanceMetres = Math.round(bufferDistanceMetres);
    boundary.bufferUnit = unit;
    boundary.originalGeometry =
      boundary.originalGeometry ?? boundary.geometry.clone();

    // Transform it to UTM
    let olGeom = boundary.originalGeometry.clone() as olGeometry;
    olGeom.transform("EPSG:3857", "EPSG:4326");
    const centre = OlExtent.getCenter(olGeom.getExtent());
    const utmZone = _getUtmZone(centre[0], centre[1]);
    const utmZoneName = `utm${utmZone}`;
    proj4.defs(
      utmZoneName,
      `+proj=utm +zone=${utmZone} +datum=WGS84 +units=m +no_defs`,
    );
    register(proj4);
    olGeom.transform("EPSG:4326", utmZoneName);

    // Use JSTS to buffer it
    const jstsGeom = OLParser.read(olGeom);
    const buffered = jstsGeom.buffer(bufferDistanceMetres);
    olGeom = OLParser.write(buffered);

    // Transform it back to 3857
    olGeom.transform(utmZoneName, "EPSG:3857");

    // Update the boundary
    boundary.geometry = olGeom;
    feature.setGeometry(olGeom);
  };

  const _distanceInMetres = (distance: number, unit: BufferUnit): number => {
    switch (+unit) {
      case BufferUnit.feet:
        return distance * 0.3048;
      case BufferUnit.kilometres:
        return distance * 1000;
      case BufferUnit.miles:
        return distance * 1609.34;
      default:
        return distance;
    }
  };

  const _getUtmZone = (longitude: number, latitude: number): number => {
    // Handle the various exceptions to the evenly spaced grid
    if (latitude >= 56 && latitude < 64 && longitude >= 3 && longitude < 13) {
      return 32;
    }

    if (latitude >= 72 && latitude < 84) {
      if (longitude >= 0 && longitude < 9) {
        return 31;
      } else if (longitude >= 9 && longitude < 21) {
        return 33;
      } else if (longitude >= 21 && longitude < 33) {
        return 35;
      } else if (longitude >= 33 && longitude < 42) {
        return 37;
      }
    }

    return Math.ceil((longitude + 180) / 6);
  };

  const _convertBbox = (bbox: WfsWGS84BoundingBox) => {
    let mixX = parseFloat(bbox["ows:LowerCorner"].split(" ")[0]);
    let minY = parseFloat(bbox["ows:LowerCorner"].split(" ")[1]);
    let maxX = parseFloat(bbox["ows:UpperCorner"].split(" ")[0]);
    let maxY = parseFloat(bbox["ows:UpperCorner"].split(" ")[1]);
    return transformExtent([mixX, minY, maxX, maxY], "EPSG:4326", "EPSG:3857");
  };

  const _updateAvailableFeatureTypes = (boundaries: BoundarySelection[]) => {
    let availableFeatureTypes = [] as string[];
    if (featureTypes !== undefined) {
      availableFeatureTypes = featureTypes
        .filter((f) =>
          boundaries.some((b) =>
            (b.geometry as olGeometry).intersectsExtent(f.bbox),
          ),
        )
        .map((f) => f.typeName);
    }
    setAvailableFeatureTypes(availableFeatureTypes);
  };

  useEffect(() => {
    if (featureTypes) return;

    const isAdmin = user?.roles.includes(Roles.ADMINISTRATOR);

    const storedFeatureTypes = localStorage.getItem(
      LocalStorageKeys.FEATURE_TYPES,
    );
    if (storedFeatureTypes != null) {
      setFeatureTypes(JSON.parse(storedFeatureTypes) as FeatureType[]);
    } else if (!isMaintenance || isAdmin) {
      wfsService
        .WfsGetFeatureTypes()
        .then((featureTypes) => {
          if (featureTypes) {
            var types = featureTypes.map((t) => ({
              typeName: t["wfs:Name"],
              bbox: _convertBbox(t["ows:WGS84BoundingBox"]),
            }));
            localStorage.setItem(
              LocalStorageKeys.FEATURE_TYPES,
              JSON.stringify(types),
            );
            setFeatureTypes(types);
            _updateAvailableFeatureTypes(boundaries);
          }
        })
        .catch(() => {});
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isMaintenance]);

  useEffect(() => {
    _updateAvailableFeatureTypes(boundaries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundaries]);

  return (
    <BoundarySelectContext.Provider
      value={{
        vectorSource,
        boundaries,
        addBoundary,
        addBoundaries,
        removeBoundary,
        clearBoundaries,
        selectBoundary,
        deselectBoundary,
        bufferBoundary,
        activeTool,
        setActiveTool,
        viewExtent,
        setViewExtent,
        configBoundaryId,
        setConfigBoundaryId,
        availableFeatureTypes,
      }}
    >
      {children}
    </BoundarySelectContext.Provider>
  );
};

export default BoundarySelectProvider;
